import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from './ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { updateSessionNotes } from '../lib/airtable';

const SessionNotesModal = ({ 
  isOpen, 
  onClose, 
  attendee, 
  session, 
  attendanceRecordId,
  initialNotes = '',
  onNotesUpdated,
  readOnlyMode = false 
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  // Update notes when initialNotes changes (when modal opens)
  useEffect(() => {
    setNotes(initialNotes);
    setHasChanges(false);
  }, [initialNotes, isOpen]);

  // Track changes
  useEffect(() => {
    setHasChanges(notes !== initialNotes);
  }, [notes, initialNotes]);

  const handleSave = useCallback(async () => {
    if (!hasChanges || !attendanceRecordId) return;

    setSaving(true);
    try {
      await updateSessionNotes(attendanceRecordId, notes);
      
      toast({
        title: "Success",
        description: "Notes saved successfully",
        variant: "default",
      });
      
      // Notify parent component of the update
      if (onNotesUpdated) {
        onNotesUpdated(attendanceRecordId, notes);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error",
        description: `Failed to save notes: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [hasChanges, attendanceRecordId, notes, toast, onNotesUpdated, onClose]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        if (hasChanges && !saving && !readOnlyMode && attendanceRecordId) {
          handleSave();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, hasChanges, saving, readOnlyMode, attendanceRecordId, handleSave]);

  const handleClose = () => {
    if (hasChanges && !saving) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setNotes(initialNotes);
        setHasChanges(false);
        onClose();
      }
    } else {
      setNotes(initialNotes);
      setHasChanges(false);  
      onClose();
    }
  };

  if (!attendee || !session) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl">Session Notes</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            <div className="space-y-1">
              <div><strong>Attendee:</strong> {attendee.firstName} {attendee.lastName}</div>
              <div><strong>Session:</strong> Session {session.sessionNo} ({new Date(session.start).toLocaleDateString()})</div>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Notes
            </label>
            <Textarea
              placeholder={readOnlyMode ? "No notes available" : "Add notes about this attendee&apos;s participation in this session..."}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnlyMode}
              className="min-h-[120px] resize-none"
              maxLength={1000}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{notes.length}/1000 characters</span>
              <div className="flex gap-4">
                {!readOnlyMode && (
                  <span className="text-muted-foreground">⌘+Enter to save</span>
                )}
                {hasChanges && !readOnlyMode && (
                  <span className="text-orange-600">Unsaved changes</span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            className="h-9 px-4"
          >
            {hasChanges && !readOnlyMode ? 'Cancel' : 'Close'}
          </Button>
          {!readOnlyMode && (
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="h-9 px-4"
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save Notes'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionNotesModal;